import { ReactNode, forwardRef, useImperativeHandle, useState } from "react";
import Carousel, {
  CarouselAnnouncement,
  OverflowCarousel,
  SlideShow,
  SlideShowAnnouncement,
  SlideShowPaginationButtonLabel,
} from "@ingka/carousel";

import "@ingka/svg-icon/dist/style.css";
import "@ingka/button/dist/style.css";
import "@ingka/carousel/dist/style.css";
import "@ingka/focus/dist/style.css";

type FixaOverflowCarouselProps = {
  children?: ReactNode;
  id?: string;
  controlBtnType?:
    | "primary"
    | "emphasised"
    | "secondary"
    | "tertiary"
    | "danger"
    | "plain"
    | "image-overlay";
  "data-testid"?: string;
  slideIndex?: number;
};

export function FixaOverflowCarousel(props: FixaOverflowCarouselProps) {
  return <OverflowCarousel {...props} />;
}

/** Minimal surface of the @ingka/carousel Carousel class instance we need. */
export interface FixaCarouselHandle {
  goToIndex: (index: number) => void;
}

type FixaCarouselCommonProps = {
  ariaLabel: string;
  ariaLabelLeftBtn: string;
  ariaLabelRightBtn: string;
  children?: React.ReactElement[];
  hasNoControls?: boolean;
  id: string;
  onVisibleSlideChange?: (visibleIndices: number[]) => void;
  skipButtonText: string;
  "data-testid"?: string;
  slideIndex?: number;
  className?: string;
};

type FixaCarouselProps = FixaCarouselCommonProps &
  (
    | {
        isSlideShow: true;
        slideshowTrackAriaLabel: string;
        getLiveAnnouncement: (status: SlideShowAnnouncement) => string;
        getPaginationButtonLabel: (
          status: SlideShowPaginationButtonLabel
        ) => string;
      }
    | {
        isSlideShow?: false;
        carouselTrackAriaLabel: string;
        getLiveAnnouncement: (status: CarouselAnnouncement) => string;
      }
  );

const FixaCarousel = forwardRef<FixaCarouselHandle, FixaCarouselProps>(
  (props, ref) => {
    const {
      ariaLabel,
      ariaLabelLeftBtn,
      ariaLabelRightBtn,
      children,
      className,
      hasNoControls,
      id,
      onVisibleSlideChange,
      skipButtonText,
      slideIndex,
    } = props;
    const [activeSlideIndex, setActiveSlideIndex] = useState(slideIndex ?? 0);
    const [previousSlideIndex, setPreviousSlideIndex] = useState(slideIndex);

    if (slideIndex !== previousSlideIndex) {
      setPreviousSlideIndex(slideIndex);
      if (slideIndex !== undefined) {
        setActiveSlideIndex(slideIndex);
      }
    }

    const handleSlideChange = (index: number) => {
      setActiveSlideIndex(index);
      onVisibleSlideChange?.([index]);
    };

    useImperativeHandle(ref, () => ({ goToIndex: handleSlideChange }));

    const commonProps = {
      ariaLabel,
      children,
      className,
      "data-testid": props["data-testid"],
      id,
      nextLabel: ariaLabelRightBtn,
      previousLabel: ariaLabelLeftBtn,
      slideIndex: activeSlideIndex,
      skipButtonText,
    };

    if (props.isSlideShow) {
      return (
        <SlideShow
          {...commonProps}
          getLiveAnnouncement={props.getLiveAnnouncement}
          getPaginationButtonLabel={props.getPaginationButtonLabel}
          navigationButtonPlacement={hasNoControls ? "hidden" : "pagination"}
          onSlideChange={handleSlideChange}
          slideshowTrackAriaLabel={props.slideshowTrackAriaLabel}
        />
      );
    }

    return (
      <Carousel
        {...commonProps}
        carouselTrackAriaLabel={props.carouselTrackAriaLabel}
        getLiveAnnouncement={props.getLiveAnnouncement}
        navigationButtonPlacement={hasNoControls ? "hidden" : "inset"}
        onSlideChange={handleSlideChange}
      />
    );
  }
);
FixaCarousel.displayName = "FixaCarousel";

export default FixaCarousel;
